/**
 * Flavor Configuration
 *
 * Detects the current application flavor from environment variables
 * and provides feature flags for conditional behavior.
 *
 * Flavors:
 *   - "global" → EZShift (international, supports en + he)
 *   - "idf"    → טוחן אחי (IDF-specific, Hebrew-only, restricted feature set)
 */

export type Flavor = 'global' | 'idf';

export interface FeatureFlags {
  /** Whether the language switcher is available */
  readonly languageSwitcher: boolean;
  /** Whether the user can change the organization name */
  readonly editableOrgName: boolean;
  /** Whether export features are available */
  readonly exportEnabled: boolean;
  /** Whether workspace import is available */
  readonly importEnabled: boolean;
  /** Default app name */
  readonly appName: string;
  /** Default locale */
  readonly defaultLocale: string;
  /** Supported locales */
  readonly supportedLocales: readonly string[];
  /** Is IDF flavor */
  readonly isIdf: boolean;
}

const FLAVOR: Flavor = (import.meta.env.VITE_FLAVOR as Flavor) || 'global';

const FLAGS: Record<Flavor, FeatureFlags> = {
  global: {
    languageSwitcher: true,
    editableOrgName: true,
    exportEnabled: true,
    importEnabled: true,
    appName: import.meta.env.VITE_APP_NAME || 'EZShift',
    defaultLocale: import.meta.env.VITE_DEFAULT_LOCALE || 'en',
    supportedLocales: (import.meta.env.VITE_SUPPORTED_LOCALES || 'en').split(','),
    isIdf: false,
  },
  idf: {
    languageSwitcher: false,
    editableOrgName: false,
    exportEnabled: true,
    importEnabled: false,
    appName: 'טוחן אחי',
    defaultLocale: 'he',
    supportedLocales: ['he'],
    isIdf: true,
  },
};

/** Current application flavor */
export const flavor: Flavor = FLAVOR;

/** Feature flags for the current flavor */
export const features: FeatureFlags = FLAGS[FLAVOR];
